import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Music2, ChevronLeft, ChevronRight } from 'lucide-react';
import { BreadcrumbNav } from '../../components/layout/BreadcrumbNav.js';
import { SongCard } from '../../components/song/SongCard.js';
import { genresService } from '../../services/genres.service.js';

function GenreSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-4 w-32 bg-surface-hover rounded mb-6" />
      <div className="h-8 w-48 bg-surface-hover rounded mb-2" />
      <div className="h-4 w-72 bg-surface-hover rounded mb-8" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="rounded-xl overflow-hidden bg-surface">
            <div className="aspect-square bg-surface-hover" />
            <div className="p-3 space-y-2">
              <div className="h-4 bg-surface-hover rounded w-3/4" />
              <div className="h-3 bg-surface-hover rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GenrePage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);

  const { data: genreData, isLoading: genreLoading } = useQuery({
    queryKey: ['genre', slug],
    queryFn: () => genresService.getGenreBySlug(slug!),
    enabled: !!slug,
  });

  const { data: songsData, isLoading: songsLoading } = useQuery({
    queryKey: ['genre', slug, 'songs', page],
    queryFn: () => genresService.getGenreSongs(slug!, { page, limit: 12 }),
    enabled: !!slug,
  });

  const genre = genreData?.data;
  const songs = songsData?.data ?? [];
  const meta = (songsData as any)?.meta;
  const totalPages = meta?.totalPages ?? 1;

  if (genreLoading) {
    return <GenreSkeleton />;
  }

  if (!genre) {
    return (
      <div className="text-center py-16">
        <Music2 size={64} className="mx-auto mb-4 text-text-muted opacity-50" />
        <h1 className="text-2xl font-bold text-text-primary mb-2">Genero no encontrado</h1>
        <p className="text-text-secondary">El genero que buscas no existe.</p>
        <Link to="/" className="text-primary hover:text-primary/80 mt-4 inline-block">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const breadcrumbs = [{ label: 'Generos', to: '/genres' }, { label: genre.name }];

  return (
    <>
      <Helmet>
        <title>{genre.name} - Canciones | LyricsHub</title>
        <meta
          name="description"
          content={`Letras de canciones del genero ${genre.name}. Descubre las mejores letras de ${genre.name} en LyricsHub.`}
        />
      </Helmet>

      <BreadcrumbNav items={breadcrumbs} />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-text-primary mb-2 flex items-center gap-2">
          <Music2 size={28} className="text-primary" />
          {genre.name}
        </h1>
        {(genre as any).description && (
          <p className="text-text-secondary max-w-2xl">{(genre as any).description}</p>
        )}
      </div>

      {/* Songs Grid */}
      {songsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-pulse">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="rounded-xl overflow-hidden bg-surface">
              <div className="aspect-square bg-surface-hover" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-surface-hover rounded w-3/4" />
                <div className="h-3 bg-surface-hover rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : songs.length === 0 ? (
        <p className="text-center text-text-muted py-12">
          No hay canciones en este genero todavia.
        </p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {songs.map((song: any) => (
            <SongCard
              key={song.id}
              slug={song.slug}
              title={song.title}
              artistName={song.artistName}
              artistSlug={song.artistSlug}
              coverUrl={song.coverUrl}
              genreName={song.genreName}
              viewCount={song.viewCount}
              variant="vertical"
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-2 rounded-lg hover:bg-surface-hover text-text-muted disabled:opacity-40 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
            let pageNum: number;
            if (totalPages <= 7) {
              pageNum = i + 1;
            } else if (page <= 4) {
              pageNum = i + 1;
            } else if (page >= totalPages - 3) {
              pageNum = totalPages - 6 + i;
            } else {
              pageNum = page - 3 + i;
            }
            return (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                  page === pageNum
                    ? 'bg-primary text-white'
                    : 'text-text-secondary hover:bg-surface-hover'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="p-2 rounded-lg hover:bg-surface-hover text-text-muted disabled:opacity-40 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </>
  );
}
